import { useState } from 'react';
import { createPortal } from 'react-dom';

import { useTranslation } from 'react-i18next';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { AlertTriangle, Copy, Check, X as XIcon } from 'lucide-react';

import { toast_success, toast_error } from '@/libs/toast-wrappers';
import { CreateApiKeyResponse } from '@/api/api-keys';

import { Button } from '@/components/ui/button';

type ApiKeyDisplayModalProps = {
  apiKey: CreateApiKeyResponse | null;
  isOpen: boolean;
  onClose: () => void;
};

export function ApiKeyDisplayModal({ apiKey, isOpen, onClose }: ApiKeyDisplayModalProps) {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);

  const handleCopyKey = async () => {
    if (!apiKey) return;

    try {
      await navigator.clipboard.writeText(apiKey.api_key);
      setIsCopied(true);
      toast_success(t('api-keys-copied'));
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      toast_error(t('api-keys-copy-failed'));
    }
  };

  const handleClose = () => {
    setIsCopied(false);
    onClose();
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!apiKey) return null;

  return createPortal(
    <Dialog open={isOpen} onClose={handleClose} className="relative z-100">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/25 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-sm bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-lg transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
              <Button variant="ghost" size="icon" onClick={handleClose}>
                <span className="sr-only">Close</span>
                <XIcon />
              </Button>
            </div>

            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:size-10">
                <AlertTriangle className="size-6 text-yellow-600" aria-hidden="true" />
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <DialogTitle
                  as="h3"
                  className="text-base font-semibold text-gray-900 dark:text-gray-100"
                >
                  {t('api-keys-modal-title')}
                </DialogTitle>
              </div>
            </div>

            <div className="mt-4 space-y-4">
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium">
                  {t('api-keys-modal-warning')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('api-keys-modal-key-label')}
                </label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-3 bg-gray-100 dark:bg-gray-700 rounded-md text-sm font-mono break-all">
                    {apiKey.api_key}
                  </code>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => void handleCopyKey()}
                    className="shrink-0"
                  >
                    {isCopied ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="text-sm text-gray-600 dark:text-gray-400">
                <p>
                  <strong>{t('api-keys-modal-name')}:</strong> {apiKey.name}
                </p>
                <p>
                  <strong>{t('api-keys-modal-created')}:</strong> {formatDate(apiKey.created_at)}
                </p>
              </div>
            </div>

            <div className="mt-5 sm:mt-4 flex justify-end">
              <Button onClick={handleClose}>{t('api-keys-modal-close')}</Button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>,
    document.body
  );
}
